import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Loader2, ArrowRight, ClipboardList, CalendarDays, Receipt, Truck } from "lucide-react";

function greetingFor(h) {
  if (h < 12) return { word: "Good morning", img: "morning" };
  if (h < 17) return { word: "Good afternoon", img: "afternoon" };
  return { word: "Good evening", img: "evening" };
}

export function useWelcome() {
  const h = new Date().getHours();
  const tod = h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    const prev = localStorage.getItem("htss_last_inspiration") || "";
    api.get("/dashboard/welcome", { params: { tod, prev } })
      .then((r) => { if (alive) { setData(r.data); if (r.data?.inspiration) localStorage.setItem("htss_last_inspiration", r.data.inspiration); } })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tod]);
  return { data, loading };
}

export function WelcomeHeader({ data, loading }) {
  const { user } = useAuth();
  const first = (user?.name || user?.email || "there").split(" ")[0].split("@")[0];
  const g = greetingFor(new Date().getHours());
  return (
    <div className="min-w-0" data-testid="dashboard-welcome">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#1D1D1F] tracking-tight flex items-center gap-2" data-testid="welcome-greeting">
        <img src={`/brand/illustrations/greet-${g.img}.webp`} alt="" className="w-9 h-auto shrink-0" /> {g.word}, {first}
      </h1>
      <p className="mt-2 text-[#6E6E73] text-sm sm:text-base leading-relaxed max-w-3xl" data-testid="welcome-message">
        {loading
          ? <span className="inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> Getting your day ready…</span>
          : (data?.message || "Let's make today a good one.")}
      </p>
      {!loading && data?.inspiration && (
        <div className="mt-2 flex items-center gap-2 text-sm italic text-[#6E6E73]" data-testid="welcome-inspiration">
          <Sparkles className="w-4 h-4 shrink-0 text-amber-500" /> {data.inspiration}
        </div>
      )}
    </div>
  );
}

const TILE = {
  jobs: { bg: "bg-[#FF6B2C]", Icon: ClipboardList },
  shifts: { bg: "bg-[#5B4CF0]", Icon: CalendarDays },
  expenses: { bg: "bg-[#F5A623]", Icon: Receipt },
  vehicles: { bg: "bg-[#1EA7F5]", Icon: Truck },
};

function Sparkline({ points, testid }) {
  if (!points || points.length < 2) return <div className="text-[11px] text-white/70 mt-1" data-testid={testid}>Trend builds daily</div>;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals), max = Math.max(...vals), span = max - min || 1;
  const W = 100, H = 28;
  const xy = vals.map((v, i) => [(i / (vals.length - 1)) * W, H - 3 - ((v - min) / span) * (H - 6)]);
  const d = xy.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const delta = vals[vals.length - 1] - vals[0];
  return (
    <div className="mt-1 flex items-end gap-2" data-testid={testid}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-24 h-7" preserveAspectRatio="none" aria-hidden>
        <path d={`${d} L${W},${H} L0,${H} Z`} fill="rgba(255,255,255,0.18)" />
        <path d={d} fill="none" stroke="white" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      </svg>
      <span className="text-[11px] font-semibold text-white/90 whitespace-nowrap">{delta > 0 ? "+" : ""}{delta.toLocaleString()} this week</span>
    </div>
  );
}

export function WelcomeActions({ data }) {
  const { cardAllowed } = useAuth();
  const actions = (data?.actions || []).filter((a) => cardAllowed(`tile_${a.key}`));
  if (!actions.length) return null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 h-full" data-testid="welcome-actions-card">
      {actions.map((a, i) => {
        const t = TILE[a.key] || TILE.jobs;
        return (
          <Link key={i} to={a.to} title={a.label} data-testid={`welcome-action-${i}`}
            className={`group relative overflow-hidden rounded-[18px] ${t.bg} text-white p-4 sm:p-5 flex flex-col justify-between min-h-[120px] shadow-card hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow] duration-200`}>
            <t.Icon className="absolute -right-3 -bottom-3 w-20 h-20 opacity-15" strokeWidth={1.5} />
            <div className="text-4xl sm:text-5xl font-bold tracking-tight leading-none" data-testid={`welcome-action-count-${i}`}>{(a.count ?? 0).toLocaleString()}</div>
            <div className="mt-3 flex items-center gap-1 text-sm font-medium text-white/90">
              {a.title || a.label}
              <ArrowRight className="w-3.5 h-3.5 shrink-0 opacity-80 group-hover:translate-x-0.5 transition-transform" />
            </div>
            <Sparkline points={a.trend} testid={`welcome-action-trend-${i}`} />
          </Link>
        );
      })}
    </div>
  );
}
